package guide.kuki.app.engine

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable

/**
 * JSON structure for radial map grid data files
 * (radial_map_100min.json / radial_map_60min.json).
 */
@Serializable
data class RadialMapData(
    val sections: List<Int>,
    val radials: List<List<List<Double>>>,
)

/**
 * JSON structure for a single named ring entry in names.json. Keyed in the
 * file by ring code (`a1`, `i2`, `ka1`, …).
 *
 * - [name] — the address name shown in block addresses (e.g. "Ina").
 * - [en] / [rar] — the cultural meaning (or story title) in English and
 *   Cook Islands Maori (ISO 639-3 `rar`).
 * - [story] — id of a story fragment in `files/stories/<story>.html`, if any.
 * - [image] — filename (with extension) of a bundled image in `files/names/`.
 *
 * A ring is "curated" when [en] is non-empty.
 */
@Serializable
data class NameEntry(
    val name: String = "",
    val en: String = "",
    val rar: String = "",
    val story: String = "",
    val image: String = "",
)

/**
 * A curated ring: its ring code paired with its [NameEntry]. Produced by
 * [RadialMap.namedRings] for the Names/Stories menu pages.
 */
data class RingName(
    val code: String,
    val entry: NameEntry,
)

/**
 * Converts parsed [RadialMapData] into a [RadialMap] engine instance.
 */
fun RadialMapData.toRadialMap(): RadialMap {
    val radialArrays = radials.map { radial ->
        radial.map { pt -> doubleArrayOf(pt[0], pt[1]) }
    }
    return RadialMap(radialArrays, sections)
}
